import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import styled from 'styled-components';
import classNames from 'classnames';
import { Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { format } from 'date-fns';
import { isEmpty } from 'lodash';

import { theme, DatePicker, Icon } from '../../../components';

import Bounds from './Bounds';
import TimeSelectorButton from './TimeSelectorButton';
import AdvancedSelector from './AdvancedSelector';
import { Container } from './WeeklyTimeSelector';

class SpecialDaySelector extends React.Component {
  constructor(props) {
    super(props);

    this.timeslotRefs = {};

    this.state = {
      selectedDate: new Date(),
      showCalendar: false,
      selectedRows: [],
      selectedColumns: [],
      selectionBox: {},
      offsetFromRoot: undefined,
      advConfSetting: false,
    };
  }

  updateComponentOffset = () => {
    // Calculate the component offset from the page so we can draw the selection
    // box in the right place
    const ref = this.componentRef;
    const pageX = ref.getBoundingClientRect().left + window.pageXOffset;
    const pageY = ref.getBoundingClientRect().top + window.pageYOffset;
    this.setState({ timeSelectorOffset: [pageX, pageY] });
  };

  onMouseDown = e => {
    e.preventDefault();
    if (e.button !== 0) {
      // A button other than the main button was pressed; ignore
      return;
    }

    const { specialDays } = this.props;
    const { advConfSetting } = this.state;

    // Get the position of the component in preparation of future calculations
    this.updateComponentOffset();
    // Get row and column bounds to determine what's selected
    this.updateRowAndColumnBounds();

    // Set starting bounds of the selection box
    const { pageX: x, pageY: y } = e;
    const selectionBox = { start: { x, y }, end: { x, y } };

    // Determine whether this selection is to enable or disable based on the
    // state of the starting element
    const startTarget = Object.keys(this.timeslotRefs).find(key => {
      if (!this.timeslotRefs[key]) return;
      return Bounds.fromElement(this.timeslotRefs[key]).contains({ x, y });
    });
    if (startTarget) {
      const [startDay, startTime] = startTarget.split('~');
      if (advConfSetting) {
        selectionBox.toValue =
          specialDays[startDay][startTime] === 'AutoConfirm'
            ? 'Available'
            : 'AutoConfirm';
      } else {
        selectionBox.toValue =
          specialDays[startDay][startTime] === 'Available' ||
          specialDays[startDay][startTime] === true
            ? 'Restricted'
            : 'Available';
      }
    } else {
      selectionBox.toValue = advConfSetting ? 'AutoConfirm' : 'Restricted';
    }

    // Update selected rows and columns
    const { selectedRows, selectedColumns } =
      this.getSelectedRowsAndColumns(selectionBox);

    this.setState({
      selectionBox,
      selectedRows,
      selectedColumns,
      mouseDown: true,
    });

    // Add listeners for future mouse action
    window.document.addEventListener('mousemove', this.onMouseMove);
    window.document.addEventListener('mouseup', this.onMouseUp);
  };

  onMouseMove = e => {
    e.preventDefault();

    const { selectionBox } = this.state;
    selectionBox.end = { x: e.pageX, y: e.pageY };

    const { selectedRows, selectedColumns } =
      this.getSelectedRowsAndColumns(selectionBox);

    this.setState({
      selectionBox,
      selectedRows,
      selectedColumns,
    });
  };

  onMouseUp = e => {
    e.preventDefault();

    if (e.button !== 0) {
      // A button other than the main button was pressed; ignore
      return;
    }

    window.document.removeEventListener('mousemove', this.onMouseMove);
    window.document.removeEventListener('mouseup', this.onMouseUp);

    const {
      selectionBox: { toValue },
      selectedRows,
      selectedColumns,
    } = this.state;
    this.setState({
      selectionBox: {},
      selectedRows: [],
      selectedColumns: [],
      mouseDown: false,
    });

    this.processSelection(selectedRows, selectedColumns, toValue);
  };

  updateRowAndColumnBounds = () => {
    const { specialDays } = this.props;
    this.verticalBounds = Object.entries(this.timeslotRefs)
      .filter(([key]) => key.split('~')[0] === Object.keys(specialDays)[0])
      .map(([key, element]) => {
        if (!element) return;
        const [, timeslot] = key.split('~');
        const bounds = Bounds.fromElement(element);
        return {
          timeslot,
          top: bounds.top,
          bottom: bounds.bottom,
        };
      });
    this.horizontalBounds = Object.entries(this.timeslotRefs)
      .filter(([key]) => key.split('~')[1] === '7:00')
      .map(([key, element]) => {
        if (!element) return;
        const [day] = key.split('~');
        const bounds = Bounds.fromElement(element);
        return {
          day,
          left: bounds.left,
          right: bounds.right,
        };
      });
  };

  getSelectedRowsAndColumns = selectionBox => {
    const { start, end } = selectionBox;
    const selectionBounds = Bounds.fromPoints(start, end);

    const selectedRows = (this.verticalBounds || [])
      .filter(row => {
        if (!row) return;
        return selectionBounds.intersectsRow(row);
      })
      .map(row => row.timeslot);

    const selectedColumns = (this.horizontalBounds || [])
      .filter(column => {
        if (!column) return;
        return selectionBounds.intersectsColumn(column);
      })
      .map(column => column.day);

    return { selectedRows, selectedColumns };
  };

  processSelection = (selectedRows, selectedColumns, toValue) => {
    const { updateSpecialDays } = this.props;
    const selectedTimeslots = selectedColumns
      .map(day => selectedRows.map(time => [day, time]))
      .flat(1);

    updateSpecialDays(undefined, selectedTimeslots, toValue);
  };

  toggleCalendar = () => {
    const { showCalendar } = this.state;
    this.setState({ showCalendar: !showCalendar });
  };

  static createTimesForDay = (isAvailable = false) => {
    const day = {};
    for (let i = 7; i < 24; i += 1) {
      day[`${i}:00`] = isAvailable;
      day[`${i}:30`] = isAvailable;
    }
    return day;
  };

  createSpecialDay = day => {
    const { specialDays, updateSpecialDays } = this.props;

    if (specialDays[day]) return;
    const updatedDays = {
      ...specialDays,
      [day]: SpecialDaySelector.createTimesForDay(),
    };
    updateSpecialDays(updatedDays);
  };

  deleteSpecialDay = day => {
    const { specialDays, updateSpecialDays } = this.props;
    const { [day]: deletedDay, ...otherDays } = specialDays;
    updateSpecialDays(otherDays);
  };

  toggleDay = (day, toValue) => {
    const { specialDays, updateSpecialDays } = this.props;

    const updatedDays = {
      ...specialDays,
      [day]: SpecialDaySelector.createTimesForDay(toValue),
    };
    updateSpecialDays(updatedDays);
  };

  renderSelectionBox = () => {
    const { mouseDown, selectionBox, timeSelectorOffset } = this.state;
    if (!mouseDown) {
      // Only show the selection when user is actively selecting
      return null;
    }

    const { start, end, toValue } = selectionBox;
    const selectionBoxBounds = Bounds.fromPoints(start, end);
    // We need to factor in the component offset because this element is drawn
    // Absolute to the relative parent
    const [offsetX, offsetY] = timeSelectorOffset;
    const selectionBoxStyles = {
      left: selectionBoxBounds.left - offsetX,
      width: selectionBoxBounds.right - selectionBoxBounds.left,
      top: selectionBoxBounds.top - offsetY,
      height: selectionBoxBounds.bottom - selectionBoxBounds.top,
    };
    return (
      <div
        className={classNames('time-selector__selection-box', {
          'time-selector__selection-box--available':
            toValue === 'Available' || toValue === 'AutoConfirm',
          'time-selector__selection-box--restricted': toValue === 'Restricted',
        })}
        id="selection-box"
        style={selectionBoxStyles}
      />
    );
  };

  advAutoConfirmClickHandler = e => {
    this.setState({ advConfSetting: e.target.value === 'advAutoConfEnabled' });
  };

  disableAutoConfirmToggle = () => {
    this.setState({ advConfSetting: false });
  };

  removeAllAutoConfirmSlots = () => {
    const { updateSpecialDays, specialDays } = this.props;
    const autoConfTimeslots = [];
    Object.keys(specialDays).forEach(day => {
      Object.keys(specialDays[day]).forEach(time => {
        if (specialDays[day][time] === 'AutoConfirm') {
          autoConfTimeslots.push([day, time]);
        }
      });
    });
    updateSpecialDays(undefined, autoConfTimeslots, 'Available');
  };

  handleAddDate = date => {
    this.createSpecialDay(format(date, 'yyyy-MM-dd').toString());
    this.toggleCalendar();
  };

  componentDidUpdate(prevProps, prevState) {
    const { disableAutoConfirm, noMoreShowings } = this.props;
    if (
      (disableAutoConfirm && prevState.advConfSetting) ||
      (noMoreShowings && prevProps.noMoreShowings !== noMoreShowings)
    ) {
      this.disableAutoConfirmToggle();
      if (noMoreShowings && prevProps.noMoreShowings !== noMoreShowings) {
        this.removeAllAutoConfirmSlots();
      }
    }
  }

  render() {
    const { specialDays, disableAutoConfirm, noMoreShowings } = this.props;
    const {
      showCalendar,
      selectedColumns,
      selectedRows,
      selectionBox,
      advConfSetting,
      selectedDate,
    } = this.state;

    return (
      <Container>
        <AdvancedSelector
          advConfSetting={advConfSetting}
          clickHandler={this.advAutoConfirmClickHandler}
          disableAutoConfirm={disableAutoConfirm}
          noMoreShowings={noMoreShowings}
        />
        <DaySelectorContainer
          className="special-day-selector"
          $scrollable={!isEmpty(specialDays)}
          ref={el => {
            this.componentRef = el;
          }}
        >
          {this.renderSelectionBox()}
          <StyledTable>
            <thead>
              <tr>
                <th />
                {Object.keys(specialDays)
                  .sort()
                  .map(day => (
                    <StyledRowHeader key={day}>
                      <span>
                        <StyledCloseIcon
                          data-cy="remove-special-days"
                          type="antdCloseOutlined"
                          onClick={() => this.deleteSpecialDay(day)}
                        />
                      </span>

                      <h4
                        data-cy={`special-day-${day}`}
                        className={
                          (selectedColumns.includes(day) &&
                            (selectionBox.toValue === true ||
                            selectionBox.toValue === 'Available' ||
                            selectionBox.toValue === 'AutoConfirm'
                              ? 'weekly-time-selector__label--available'
                              : 'weekly-time-selector__label--restricted')) ||
                          ''
                        }
                      >
                        {day}
                      </h4>
                      <button
                        className="time-selector__toggle-day time-selector__toggle-day--off"
                        onClick={() => {
                          this.toggleDay(day, 'Restricted');
                        }}
                        data-cy={`${day}-set-restricted`}
                      />
                      <button
                        className="time-selector__toggle-day time-selector__toggle-day--on"
                        onClick={() => {
                          this.toggleDay(day, 'Available');
                        }}
                        data-cy={`${day}-set-available`}
                      />
                    </StyledRowHeader>
                  ))}

                <th>
                  <StyledButton
                    data-cy="add-special-days"
                    icon={<PlusOutlined />}
                    onClick={this.toggleCalendar}
                  >
                    Special Day
                  </StyledButton>
                  {showCalendar && (
                    <StyledDatePicker
                      disabledDate={currentDate =>
                        moment().add(-1, 'days') >= currentDate
                      }
                      onChange={value => {
                        this.setState({
                          selectedDate: value,
                        });
                      }}
                      value={selectedDate}
                      renderExtraFooter={() => (
                        <StyledFooter>
                          <Button
                            data-cy="now-calendar-button"
                            size="small"
                            type="link"
                            onClick={() => this.handleAddDate(new Date())}
                          >
                            Now
                          </Button>
                          <Button
                            data-cy="add-date-button"
                            size="small"
                            type="primary"
                            disabled={!selectedDate}
                            onClick={() => this.handleAddDate(selectedDate)}
                          >
                            Add
                          </Button>
                        </StyledFooter>
                      )}
                      format="yyyy-mm-dd"
                      showToday={false}
                      placement="bottomLeft"
                      open={showCalendar}
                    />
                  )}
                </th>
              </tr>
            </thead>
            <tbody onMouseDown={this.onMouseDown}>
              {Object.keys(specialDays[Object.keys(specialDays)[0]] ?? {}).map(
                timeslot => (
                  <tr
                    key={timeslot}
                    className="weekly-time-selector__time-interval"
                  >
                    <td key={`${timeslot}-label`}>
                      <h4
                        className={
                          (selectedRows.includes(timeslot) &&
                            (selectionBox.toValue === true ||
                            selectionBox.toValue === 'Available' ||
                            selectionBox.toValue === 'AutoConfirm'
                              ? 'weekly-time-selector__label--available'
                              : 'weekly-time-selector__label--restricted')) ||
                          ''
                        }
                      >
                        {moment(timeslot, 'H:mm').format('h:mm a')}
                      </h4>
                    </td>

                    {Object.entries(specialDays)
                      .sort()
                      .map(([day, dayAvailabilities]) => (
                        <StyledTimeSelector
                          className="weekly-time-selector__timeslot"
                          key={`${day}~${timeslot}`}
                        >
                          <TimeSelectorButton
                            status={
                              this.props.autoConfirm
                                ? this.props.mapAutoConfAvailabilities(
                                    dayAvailabilities,
                                  )[timeslot]
                                : dayAvailabilities[timeslot]
                            }
                            showIcon
                            day={day}
                            timeslot={timeslot}
                            ref={el => {
                              // TODO: We only need refs for the first row and column
                              this.timeslotRefs[`${day}~${timeslot}`] = el;
                            }}
                          />
                        </StyledTimeSelector>
                      ))}
                  </tr>
                ),
              )}
            </tbody>
          </StyledTable>
        </DaySelectorContainer>
      </Container>
    );
  }
}

SpecialDaySelector.propTypes = {
  specialDays: PropTypes.objectOf(
    PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.string,
        // 'Available',
        // 'Restricted',
        // 'AutoConfirm',
        PropTypes.bool,
      ]),
    ),
  ),
  unavailableDays: PropTypes.arrayOf(PropTypes.string),
  updateSpecialDays: PropTypes.func,
};

export default SpecialDaySelector;

const StyledButton = styled(Button)`
  && {
    font-weight: 500;
    font-size: 14px;
    border-radius: 2px;
  }

  :hover,
  :focus {
    .anticon-plus {
      color: ${theme.blue5};
    }
  }
`;

const DaySelectorContainer = styled.div`
  height: fit-content;
  ${({ $scrollable }) => $scrollable && 'overflow-x: scroll;'}
`;

const StyledDatePicker = styled(DatePicker)`
  .ant-picker {
    width: 0px;
    height: 0px;
    visibility: hidden;
  }
`;

const StyledFooter = styled.div`
  padding: 10px 5px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledTimeSelector = styled.td`
  background: white;
  margin: 0px 10px !important;
`;

const StyledRowHeader = styled.th`
  && {
    background: white;
    padding: 10px;
    margin: 10px;
  }
  span {
    display: flex;
    justify-content: flex-end;
  }
`;

const StyledCloseIcon = styled(Icon)`
  width: fit-content;
  svg {
    stroke-width: 25;
    stroke: ${theme.gray8};
    color: ${theme.gray8};
  }
`;

const StyledTable = styled.table`
  && {
    border-collapse: separate;
    border-spacing: 20px 0;
  }
`;
